'use client';

import { allSortMethod } from '@/constants/all-sort-method';
import useIsMobile from '@/hooks/use-is-mobile';
import { PopoverTrigger } from '@radix-ui/react-popover';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRef, useState } from 'react';
import { Popover, PopoverContent } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const ProductSort = props => {
  const isMobile = useIsMobile();
  const { availableSortMethods, sortState, isSearchPage = false } = props;
  const triggerRef = useRef();
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const excludePaginationPathParts = pathname.split('/').filter((x, i, arr) => {
    return !!x && x !== 'p' && (!/\d+/.test(x) || (/\d+/.test(x) && i > 0 && arr[i - 1] !== 'p'));
  });

  const sortMethodsFromConfig = availableSortMethods
    ? availableSortMethods
        .map(method => {
          const { value, label } = method;

          if (value !== 'price') {
            return {
              id: `sortItem.${value}`,
              text: label,
              attribute: value,
              direction:
                value === 'high_to_low' || value === 'most_reviews' || value === 'created_at' || value === 'best_seller'
                  ? 'DESC'
                  : 'ASC',
            };
          }
        })
        .filter(method => !!method)
    : [];

  const generateLinkHref = (attribute, prevActive) => {
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete('sort_by');
    const sortQuery = allSortMethod.find(x => x.sortAttribute === attribute)?.sortQuery;

    if (!prevActive && sortQuery) {
      nextSearchParams.append('sort_by', sortQuery);
    }

    if (isSearchPage) {
      nextSearchParams.delete('page');
    }

    const nextQueryStr = nextSearchParams.toString();

    if (isSearchPage) {
      return `/search.html?${nextQueryStr}`;
    }

    return `/${excludePaginationPathParts.join('/')}` + (nextQueryStr ? `?${nextQueryStr}` : '');
  };

  const sortElements = (
    <ul className='bg-white'>
      {Array.from(sortMethodsFromConfig, (sortItem, i) => {
        const { attribute, direction } = sortItem;

        const isActive = sortState.sortAttribute === attribute && sortState.sortDirection === direction;
        const key = `${attribute}--${direction}`;

        return (
          <li
            key={key}
            className='px-3'
          >
            <Link
              onClick={() => {
                setOpen(false);
              }}
              className={clsx(
                i !== 0 && 'border-t-1 border-solid border-E7DED5',
                'flex w-full items-center justify-between gap-x-5 py-[15px]',
              )}
              href={generateLinkHref(attribute, isActive)}
            >
              <span className='text-sm leading-5'>{sortItem.text}</span>
              <div
                className={clsx(
                  ' h-5 w-5 ',
                  'border-1 border-solid ',
                  'flex items-center justify-center',
                  isActive ? 'border-80583F bg-80583F' : 'border-E7DED5',
                )}
              >
                {isActive ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='12'
                    fill='none'
                    viewBox='0 0 15 12'
                  >
                    <path
                      stroke='#fff'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='M13.5 1 5.1 11 1.5 7.25'
                    />
                  </svg>
                ) : null}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );

  const sortTriggerBtn = (
    <button
      priority={'low'}
      className='flex h-[50px] flex-1 items-center justify-center gap-x-2.5 bg-f8f3ef text-base leading-4 lg_h-11 lg_w-[174px] lg_flex-none'
      ref={triggerRef}
      type='button'
    >
      <span className='text-base leading-6 text-80583F'>SORT BY</span>
      <span
        className={clsx(
          ' relative h-3 w-3 ',
          'before_content-[""] after_content-[""]',
          'before_h-0.5 before_w-full after_h-0.5 after_w-full',
          'before_bg-80583F',
          'before_absolute before_left-1/2 before_top-1/2 before_-translate-x-1/2 before_-translate-y-1/2',
          'after_absolute after_left-1/2 after_top-1/2 after_-translate-x-1/2 after_-translate-y-1/2',
          'after_rotate-90',
          open ? 'after_bg-transparent' : 'after_bg-80583F',
        )}
      />
    </button>
  );

  return isMobile ? (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>{sortTriggerBtn}</PopoverTrigger>
      <PopoverContent className='shadow-md'>{sortElements}</PopoverContent>
    </Popover>
  ) : (
    <TooltipProvider>
      <Tooltip
        open={open}
        onOpenChange={setOpen}
        delayDuration={200}
      >
        <TooltipTrigger
          onClick={e => {
            e.preventDefault();
          }}
          asChild
        >
          {sortTriggerBtn}
        </TooltipTrigger>
        <TooltipContent
          onPointerDownOutside={e => {
            if (triggerRef.current?.contains(e.target)) {
              e.preventDefault();
            }
          }}
          side='bottom'
          className='border-none p-0 shadow-none'
        >
          {sortElements}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ProductSort;
